import { Button } from 'components/atoms';
import { Form } from 'components/organisms';
import { videoInitialValues } from 'components/organisms/forms/video/VideoForm';
import { FormikHelpers } from 'formik';
import { useMutateOptions } from 'hooks';
import { useParamId } from 'hooks/useParamId';
import middleware from 'middleware';
import { useTranslation } from 'react-i18next';
import { PiVinylRecord, PiX } from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import { STATUS } from 'services/types';
import { useGetVideo, usePutVideo } from 'services/video';
import {
  useDeleteVideoFile,
  usePostVideoChunkedFile,
  usePutVideoChunkedFile,
} from 'services/video/file';
import { IVideoFilePath, IVideoMutationForm } from 'services/video/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { getLastPathname } from 'utils/navbar/getParentPathname';
import { onErrorShowToast } from 'utils/service/errorHandler';

const EditVideo = () => {
  const videoId = useParamId('video');
  const { pathname } = useLocation();

  const { t } = useTranslation();
  const navigate = useNavigate();

  const getVideo = useGetVideo(videoId);

  const updateVideo = usePutVideo();

  const addVideoChunkFile = usePostVideoChunkedFile();
  const updateVideoChunkFile = usePutVideoChunkedFile();
  const deleteVideoFile = useDeleteVideoFile(videoId);

  const mutateOptions = useMutateOptions<IVideoMutationForm>();

  const backPathname = `/video/${videoId}/${getLastPathname(pathname)}`;

  const onVideoFileMutationSuccess = async (
    formikHelpers: FormikHelpers<IVideoMutationForm>,
    value?: IVideoFilePath,
  ) => {
    formikHelpers.setFieldValue('originalVideoLink', value?.originalVideoLink);
    formikHelpers.setFieldValue('file', undefined);
    navigate(backPathname, { replace: true });
  };

  const areFileLinksExists = (links: (string | undefined | null)[]) =>
    !!links.find(l => !!l?.length);

  const mutateVideoFile = async (
    values: IVideoMutationForm,
    formikHelpers: FormikHelpers<IVideoMutationForm>,
  ) => {
    const initialValues =
      getVideo.data && middleware.video.queryToForm(getVideo.data);
    if (initialValues) {
      const { originalVideoLink } = initialValues;

      if (values.file && !areFileLinksExists([originalVideoLink])) {
        await addVideoChunkFile.mutateAsync(
          { file: values.file, videoId, version: values.version },
          {
            onError: error => onErrorShowToast(error, 'toast-message.add-file'),
            onSuccess: value =>
              onVideoFileMutationSuccess(formikHelpers, value),
          },
        );
      }

      if (values.file && areFileLinksExists([originalVideoLink])) {
        await updateVideoChunkFile.mutateAsync(
          { file: values.file, videoId, version: values.version },
          {
            onError: error =>
              onErrorShowToast(error, 'toast-message.change-file'),
            onSuccess: value =>
              onVideoFileMutationSuccess(formikHelpers, value),
          },
        );
      }
      if (
        !values.file &&
        areFileLinksExists([originalVideoLink]) &&
        !areFileLinksExists([values.originalVideoLink])
      ) {
        await deleteVideoFile.mutateAsync(undefined, {
          onError: error =>
            onErrorShowToast(error, 'toast-message.delete-file'),
          onSuccess: () => onVideoFileMutationSuccess(formikHelpers),
        });
      }
    }

    navigate(backPathname, { replace: true });
  };

  const onEditVideo = async (
    values: IVideoMutationForm,
    formikHelpers: FormikHelpers<IVideoMutationForm>,
  ) => {
    await updateVideo.mutateAsync(
      {
        ...middleware.video.formToQuery(values),
        waitingForFile: !!values.file && values.status !== STATUS.draft,
        videoId: videoId,
        version: values.version,
      },
      {
        ...mutateOptions(formikHelpers),
        onSuccess: value =>
          mutateVideoFile({ ...values, version: value.version }, formikHelpers),
      },
    );
  };

  return (
    <Form.Video
      i18nextPrefix='video'
      initialValues={
        getVideo.data
          ? middleware.video.queryToForm(getVideo.data)
          : videoInitialValues
      }
      backPath={backPathname}
      status={{ isLoading: getVideo.isLoading }}
      customBackItem={
        <Button
          type='button'
          onlyIcon
          onClick={() => {
            navigate(backPathname, { replace: true });
          }}>
          <Button.Description description={t('button.cancel')} />

          <Button.Icon icon={<PiX />} />
        </Button>
      }
      onSubmit={onEditVideo}
      topbarContent={
        <Button type='submit' variant={BUTTON_VARIANT.submit}>
          <Button.Text text={t('button.save')} />
          <Button.Icon icon={<PiVinylRecord />} />
        </Button>
      }
    />
  );
};

export default EditVideo;
